import { useMemo } from 'react';
import usePath from '../../../../components/Hooks/usePath';
import { useModalState } from '../../../../hooks/useModalState';
import PATHS from '../../../../routes/routes.const';
import { Prices } from '../../../products/prices/interfaces/productPricesTooltip.interfaces';
import useProductPricesRenderer from '../../../products/prices/useProductPricesRenderer';
import {
  OrderProductPrice,
  UseOrderProductPriceV2Props,
} from '../product/types/orderProductPriceV2.types';

const useOrderProductPriceV2 = ({ grid, isFinishedOrder }: UseOrderProductPriceV2Props) => {
  const { isPath } = usePath();
  const { isOpen, closeModal, openModal } = useModalState();

  const prices: OrderProductPrice[] = useMemo(() => {
    const filteredGrid =
      isPath(PATHS.main.orders.finished.path) && isFinishedOrder
        ? grid.filter((item) => !!item.orderItemQuantity && item.orderItemQuantity > 0)
        : grid.filter(
            (item) =>
              (item.available !== false && !!item.productSizeActive) ||
              (!!item.orderItemQuantity && item.orderItemQuantity > 0),
          );

    const gridToPrice = isFinishedOrder && filteredGrid.length === 0 ? grid : filteredGrid;

    return gridToPrice.map((gridItem) => ({
      orderItemQuantity: gridItem.orderItemQuantity,
      productSizeName: gridItem.productSizeName,
      promotionalPrice: gridItem.orderPromotionalPrice,
      retailPrice: gridItem.orderRetailPrice,
      wholesalePrice: gridItem.orderWholeSalePrice,
      priceComposition: gridItem.priceComposition,
    }));
  }, [grid, isFinishedOrder, isPath]);

  const hasOrderItemQuantity = prices.some(
    (price) => !!price.orderItemQuantity && price.orderItemQuantity > 0,
  );
  const { pricesConfig, showInfoTooltip, hasManyPrices } = useProductPricesRenderer({
    prices,
    hasOrderItemQuantity,
    showAllPrices: true,
  });

  const tooltipPrices: Prices[] = useMemo(
    () =>
      prices.map((price) => ({
        sizeName: price.productSizeName,
        wholesalePrice: price.wholesalePrice,
        promotionalPrice: price.promotionalPrice,
        priceComposition: price.priceComposition?.map((composition) => ({
          label: composition.label,
          price: composition.price,
        })),
      })),
    [prices],
  );

  return {
    pricesConfig,
    tooltipPrices,
    hasManyPrices,
    showInfoTooltip,
    isProductPriceTooltipOpen: isOpen,
    openProductPriceTooltip: openModal,
    closeProductPriceTooltip: closeModal,
  };
};

export default useOrderProductPriceV2;
